import { ResultsService } from '../results.service'
import { Component, TrackByFunction } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { NodeService } from '../../shared/services/node.service'
import { map, take, tap, flatMap, shareReplay } from 'rxjs/operators'
import {
  DROPDOWN_NAME_TYPE,
  SegmentDropdownOptionSelected,
  SegmentEvents,
  DROPDOWN_OPTION_TYPE
} from '../../shared/analytics/models/segment-events.model'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import { AdComponentInputs } from '../../shared/ads/ad/ad-component.interface'
import { AdConfig, AdTargeting } from '../../shared/ads/advertising-service/ad-config'
import { Observable } from 'rxjs'
import {
  AD_ADHESION_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_SKIN_SIZE
} from '../../shared/ads/ads.utility'
import { environment } from '../../../environments/environment'
import { Result, ResultSet, SortDropdown } from '../interfaces/result.interfaces'
import { mapResultSelectOptions } from './result.utility'

type AdSlotType = 'skin' | 'flex-large' | 'flex-small' | 'rectangle' | 'adhesion'

/**
 * Still used on the old results/7556259-2022-big-ten-championships/32069 route,
 * which ONLY applies to results for events that don't have event-hubs.
 */
@Component({
  selector: 'flo-result',
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss']
})
export class ResultComponent {
  public result$ = this.route.params.pipe(
    flatMap(params => {
      return this.resultsService.result$.pipe(
        take(1),
        map(result => {
          return {
            ...result.data,
            resultSet: result.data.result_sets,
            filterValue: result.data.result_sets.map((resultSet: ResultSet) => resultSet.filter),
            sort: params.resultSetId
          }
        }),
        tap(result => {
          if (result) {
            this.nodeService.applyNodeMetaToPage(result)
            if (result.meta_result_page_title) {
              this.nodeService.setTitle(result.meta_result_page_title)
            }
            if (result.meta_result_page_description) {
              this.nodeService.setTitle(result.meta_result_page_description)
            }
          }
        })
      )
    }),
    map(resultObj => {
      const content = resultObj.resultSet
        .filter((resultSet: ResultSet) => {
          return Number(resultObj.sort) ? resultSet.id.toString() === resultObj.sort.toString() : resultObj.resultSet
        })
        .map((result: Result) => {
          return {
            ...result,
            showContent: !resultObj.should_show_read_more
          }
        })

      return {
        premium: resultObj.premium,
        id: resultObj.id,
        node: resultObj.node,
        type: resultObj.type,
        author: resultObj.author,
        aggregatedNodeIds: resultObj.aggregated_node_ids,
        aggregatedCategorySlugs: resultObj.aggregated_category_slugs,
        headerInfo: resultObj.event_data
          ? {
              assetUrl: resultObj.asset.url,
              title: `${resultObj.title} Results`,
              city: resultObj.event_data.city,
              region: resultObj.event_data.region,
              start_date_time: resultObj.event_data.start_date,
              end_date_time: resultObj.event_data.end_date
            }
          : false,
        isStandaloneEvent: !!resultObj.event_data,
        nodeId: resultObj.id,
        eventNodeId: resultObj.event_node?.id,
        assocId:
          resultObj.node &&
          resultObj.node.primary_event_or_series_association &&
          resultObj.node.primary_event_or_series_association.id,
        resultWidget: {
          content,
          filterValue: resultObj.filterValue,
          resultsData: content[0] && content[0].results,
          resultsDropdown: resultObj.filterValue.filter((result: Result) => !!result).length,
          resultSet: resultObj.resultSet,
          showDropdown: resultObj.filterValue.length > 1,
          sort: resultObj.sort,
          selectOptions: mapResultSelectOptions(resultObj)
        }
      }
    }),
    shareReplay(1)
  )

  public readonly AdComponent = import('../../shared/ads/ad/ad.component').then(m => m.AdComponent)

  private readonly adConfigMap: Map<AdSlotType, AdComponentInputs> = new Map()
  public readonly adConfigMap$: Observable<Map<AdSlotType, AdComponentInputs>> = this.result$.pipe(
    map(result => {
      const targeting: AdTargeting = {
        contentIds: result.aggregatedNodeIds,
        categorySlugs: result.aggregatedCategorySlugs,
        analyticsTracking: result
      }

      this.adConfigMap.set('skin', {
        config: {
          adConfig: AdConfig('results/post/atf', AD_SKIN_SIZE, 'results-post-ad-skin', targeting)
        }
      })

      this.adConfigMap.set('flex-large', {
        config: {
          adConfig: AdConfig(
            'results/post/728x90_flex',
            AD_LEADERBOARD_LARGE_FLEX_SIZE,
            'results-post-flex-large',
            targeting
          ),
          additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
        }
      })

      this.adConfigMap.set('flex-small', {
        config: {
          adConfig: AdConfig(
            'results/post/728x90_flex',
            AD_LEADERBOARD_SMALL_SIZE,
            'results-post-flex-small',
            targeting
          )
        }
      })

      this.adConfigMap.set('adhesion', {
        config: {
          adConfig: AdConfig('results/post/adhesion', AD_ADHESION_SIZE, 'results-post-adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      })

      return this.adConfigMap
    })
  )

  constructor(
    private readonly resultsService: ResultsService,
    private readonly route: ActivatedRoute,
    private readonly nodeService: NodeService,
    private readonly segmentService: SegmentService,
    private readonly router: Router
  ) {}

  public sortFormChange(change: SortDropdown) {
    if (change.sort !== undefined) {
      this.trackSelectOptions(change.sort as DROPDOWN_OPTION_TYPE)
      this.showResultSet(change.sort)
    }
  }

  private showResultSet(rankingSetId: string) {
    this.router.navigate([`../${rankingSetId}`], {
      relativeTo: this.route
    })
  }

  trackByResultSet: TrackByFunction<any> = (index: number, item: any) => {
    return item.id
  }

  private trackSelectOptions(sort: DROPDOWN_OPTION_TYPE) {
    // sort_by values will be dynamic in this case
    const dropdownEvent: SegmentDropdownOptionSelected = {
      name: SegmentEvents.DROPDOWN_OPTION_SELECTED,
      properties: {
        name: DROPDOWN_NAME_TYPE.SORT_BY,
        sort_by: sort,
        node: undefined
      }
    }
    this.segmentService.trackEvent(dropdownEvent)
  }
}
